import { getEnvironment, getApiBaseUrl } from '../config/domains';

// Platform endpoints - use proper environment-based API URLs
const getPlatformApiBaseUrl = (): string => {
  // Use the same environment-based API URL as other services
  return getApiBaseUrl();
};

class PlatformApiClient {
  private baseURL: string;

  constructor() {
    this.baseURL = getPlatformApiBaseUrl();
  }

  private getFullUrl(endpoint: string): string {
    // For localhost, add /api prefix to match dev-server routes
    const env = getEnvironment();
    if (env === 'localhost') {
      return `${this.baseURL}/api${endpoint}`;
    }
    return `${this.baseURL}${endpoint}`;
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    // Import dynamically to avoid circular dependency
    const { fetchAuthSession } = await import('aws-amplify/auth');
    
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
    } catch (error) {
      console.error('Failed to get auth token for platform API:', error);
      return {
        'Content-Type': 'application/json',
      };
    }
  }

  private async handleResponse<T>(response: Response, _fullUrl: string, _requestData?: any): Promise<T> {
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Platform API Error [${response.status}]:`, errorText);
      
      let errorData: any = {};
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText };
      }
      
      const error = new Error(errorData.error || `HTTP ${response.status}`) as Error & { apiError: any };
      error.apiError = {
        status: response.status,
        error: errorData.error || `HTTP ${response.status}`,
        message: errorData.message,
        details: errorData.details || []
      };
      throw error;
    }
    
    return response.json();
  }

  async get<T>(endpoint: string): Promise<T> {
    const headers = await this.getAuthHeaders();
    const fullUrl = this.getFullUrl(endpoint);
    
    const response = await fetch(fullUrl, {
      method: 'GET',
      headers,
      credentials: 'include'
    });
    
    return this.handleResponse<T>(response, fullUrl);
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    const headers = await this.getAuthHeaders();
    const fullUrl = this.getFullUrl(endpoint);
    
    const response = await fetch(fullUrl, {
      method: 'POST',
      headers,
      body: data ? JSON.stringify(data) : undefined,
      credentials: 'include'
    });
    
    return this.handleResponse<T>(response, fullUrl, data);
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    const headers = await this.getAuthHeaders();
    const fullUrl = this.getFullUrl(endpoint);
    
    const response = await fetch(fullUrl, {
      method: 'PUT',
      headers,
      body: data ? JSON.stringify(data) : undefined,
      credentials: 'include'
    });
    
    return this.handleResponse<T>(response, fullUrl, data);
  }

  async delete<T>(endpoint: string): Promise<T> {
    const headers = await this.getAuthHeaders();
    const fullUrl = this.getFullUrl(endpoint);
    
    const response = await fetch(fullUrl, {
      method: 'DELETE',
      headers,
      credentials: 'include'
    });
    
    return this.handleResponse<T>(response, fullUrl);
  }
}

// Create platform-specific API client
const platformApiClient = new PlatformApiClient();

// Import CreatePlatformData type
import type { CreatePlatformData } from '../components/platforms/CreatePlatformModal';

// Re-export for external use
export type { CreatePlatformData };

export interface Platform {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  category: string | null;
  priority: string | null;
  timezone: string | null;
  status: 'active' | 'draft' | 'inactive';
  tags: string[];
  clientCount?: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PlatformClient {
  id: string;
  platformId: string;
  name: string;
  clientType: 'web_app' | 'mobile_web' | 'mobile_app' | 'tv_app';
  status: 'active' | 'inactive';
  url?: string | null;
  domain?: string | null;
  bundleName?: string | null;
  operatingSystem?: string | null;
  appVersion?: string | null;
  clientConfig: Record<string, any>;
  metadata: Record<string, any>;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PlatformConfiguration {
  id: string;
  platformId: string;
  clientId?: string | null;
  configName: string;
  configType: 'features' | 'styling' | 'analytics' | 'targeting';
  features: Record<string, any>;
  styling: Record<string, any>;
  analytics: Record<string, any>;
  targeting: Record<string, any>;
  settings: Record<string, any>;
  isDefault: boolean;
  status: 'active' | 'inactive';
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PlatformListParams {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
}

export interface PlatformListResponse {
  platforms: Platform[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface CreateClientData {
  name: string;
  clientType: 'web_app' | 'mobile_web' | 'mobile_app' | 'tv_app';
  url?: string;
  domain?: string;
  bundleName?: string;
  operatingSystem?: string;
  appVersion?: string;
  clientConfig?: Record<string, any>;
  metadata?: Record<string, any>;
}

export interface ClientListParams {
  page?: number;
  limit?: number;
  clientType?: string;
  status?: string;
}

export interface ClientListResponse {
  clients: PlatformClient[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

class PlatformService {
  // Platform CRUD operations
  async listPlatforms(params: PlatformListParams = {}): Promise<PlatformListResponse> {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.status) queryParams.append('status', params.status);
    if (params.search) queryParams.append('search', params.search);

    const response = await platformApiClient.get<{ data: PlatformListResponse }>(`/platforms?${queryParams.toString()}`);
    console.log('🔍 Platform list raw response:', response);
    console.log('🔍 Platform list data:', response.data);
    console.log('🔍 Platform list platforms:', response.data.platforms);
    return response.data;
  }

  async createPlatform(data: CreatePlatformData): Promise<{ platform: Platform; message: string }> {
    const response = await platformApiClient.post<{ data: { platform: Platform; message: string } }>('/platforms', data);
    return response.data;
  }

  async getPlatform(id: string): Promise<Platform> {
    const response = await platformApiClient.get<{ data: { platform: Platform } }>(`/platforms/${id}`);
    return response.data.platform;
  }

  async updatePlatform(id: string, data: Partial<CreatePlatformData>): Promise<Platform> {
    const response = await platformApiClient.put<{ data: { platform: Platform } }>(`/platforms/${id}`, data);
    return response.data.platform;
  }

  async deletePlatform(id: string): Promise<void> {
    await platformApiClient.delete<void>(`/platforms/${id}`);
  }

  // Platform Client operations
  async listPlatformClients(
    platformId: string, 
    params: ClientListParams = {}
  ): Promise<ClientListResponse> {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.clientType) queryParams.append('clientType', params.clientType);
    if (params.status) queryParams.append('status', params.status);

    const response = await platformApiClient.get<{ data: ClientListResponse }>(`/platforms/${platformId}/clients?${queryParams.toString()}`);
    return response.data;
  }

  async createPlatformClient(
    platformId: string, 
    data: CreateClientData
  ): Promise<{ client: PlatformClient; message: string }> {
    const response = await platformApiClient.post<{ data: { client: PlatformClient; message: string } }>(`/platforms/${platformId}/clients`, data);
    return response.data;
  }

  async getPlatformClient(platformId: string, clientId: string): Promise<PlatformClient> {
    const response = await platformApiClient.get<{ data: { client: PlatformClient } }>(`/platforms/${platformId}/clients/${clientId}`);
    return response.data.client;
  }

  async updatePlatformClient(
    platformId: string, 
    clientId: string, 
    data: Partial<CreateClientData>
  ): Promise<PlatformClient> {
    const response = await platformApiClient.put<{ data: { client: PlatformClient } }>(`/platforms/${platformId}/clients/${clientId}`, data);
    return response.data.client;
  }

  async deletePlatformClient(platformId: string, clientId: string): Promise<void> {
    await platformApiClient.delete<void>(`/platforms/${platformId}/clients/${clientId}`);
  }

  // Platform Configuration operations
  async listPlatformConfigurations(platformId: string): Promise<PlatformConfiguration[]> {
    const response = await platformApiClient.get<{ data: { configurations: PlatformConfiguration[] } }>(`/platforms/${platformId}/configurations`);
    return response.data.configurations;
  }

  async createPlatformConfiguration(
    platformId: string, 
    data: Partial<PlatformConfiguration>
  ): Promise<PlatformConfiguration> {
    const response = await platformApiClient.post<{ data: { configuration: PlatformConfiguration } }>(`/platforms/${platformId}/configurations`, data);
    return response.data.configuration;
  }

  async updatePlatformConfiguration(
    platformId: string, 
    configId: string, 
    data: Partial<PlatformConfiguration>
  ): Promise<PlatformConfiguration> {
    const response = await platformApiClient.put<{ data: { configuration: PlatformConfiguration } }>(`/platforms/${platformId}/configurations/${configId}`, data);
    return response.data.configuration;
  }

  async deletePlatformConfiguration(platformId: string, configId: string): Promise<void> {
    await platformApiClient.delete<void>(`/platforms/${platformId}/configurations/${configId}`);
  }
}

export const platformService = new PlatformService();